import React, { useState, useEffect } from 'react';
import { 
  Scissors, UtensilsCrossed, FileText, Download, Target, Layers, CheckCircle2, 
  Calendar, BookOpen, Shirt, ChefHat, Shield, Leaf
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getCurrentUser } from '@/data/users';
import { homeEconomicsCurriculum, homeEconomicsSchemeOfWork, homeEconomicsTeacherResources } from '@/data/homeEconomicsCurriculum';
import apiService from '@/services/api';

export default function HomeEconomicsCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 9");
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedSubject, setSelectedSubject] = useState("textile");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [loading, setLoading] = useState(true);
  
  const currentUser = getCurrentUser();
  const teacherResources = homeEconomicsTeacherResources[currentUser?.id] || [];
  const curriculumData = homeEconomicsCurriculum[selectedYear];
  const schemeData = homeEconomicsSchemeOfWork[selectedYear];

  useEffect(() => {
    setLoading(false);
  }, []);

  const getSubjectIcon = (subject) => {
    if (subject === 'textile' || subject === 'Textile') return <Scissors className="h-5 w-5" />;
    return <UtensilsCrossed className="h-5 w-5" />;
  };

  const getStrandIcon = (name) => {
    if (name.includes('Design')) return <Layers className="h-5 w-5" />;
    if (name.includes('Sewing') || name.includes('Clothing')) return <Shirt className="h-5 w-5" />;
    if (name.includes('Food Choice')) return <Leaf className="h-5 w-5" />;
    if (name.includes('Preparation') || name.includes('Skills')) return <ChefHat className="h-5 w-5" />;
    return <BookOpen className="h-5 w-5" />;
  };

  const currentSubjectData = selectedSubject === 'textile' 
    ? curriculumData?.textileGarment 
    : curriculumData?.foodNutrition;

  return (
    <div className="space-y-6" data-testid="home-economics-curriculum-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <div className="flex">
              <Scissors className="h-7 w-7 text-pink-600" />
              <UtensilsCrossed className="h-7 w-7 text-orange-600 -ml-2" />
            </div>
            Home Economics / Technology
          </h1>
          <p className="text-muted-foreground mt-1">
            Textile & Garment Construction and Food & Nutrition for {selectedYear}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Year 9">Year 9</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Teacher Resources Section */}
      {teacherResources.length > 0 && (
        <Card className="border-2 border-pink-200 bg-gradient-to-r from-pink-50 to-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-pink-600" />
              Your Teaching Resources - Official Syllabi
            </CardTitle>
            <CardDescription>Curriculum documents and syllabi assigned to you</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {teacherResources.map((resource) => (
                <div key={resource.id} className="bg-white rounded-lg p-4 border border-pink-200 hover:border-pink-400 transition-colors shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${resource.subject.includes('Textile') ? 'bg-pink-100' : 'bg-orange-100'}`}>
                        {resource.subject.includes('Textile') ? (
                          <Scissors className="h-6 w-6 text-pink-600" />
                        ) : (
                          <UtensilsCrossed className="h-6 w-6 text-orange-600" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{resource.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{resource.description}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">{resource.year}</Badge>
                          <Badge variant="outline" className="text-xs">{resource.subject}</Badge>
                          <Badge variant="outline" className="text-xs">.{resource.type.toUpperCase()}</Badge>
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="shrink-0"
                      onClick={() => window.open(resource.url, '_blank')}
                      data-testid={`download-${resource.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subject Selector */}
      <div className="flex gap-4">
        <Card 
          className={`flex-1 cursor-pointer transition-all ${selectedSubject === 'textile' ? 'border-2 border-pink-500 bg-pink-50' : 'hover:border-pink-300'}`}
          onClick={() => setSelectedSubject('textile')}
        >
          <CardContent className="flex items-center gap-4 p-4">
            <div className="w-12 h-12 rounded-lg bg-pink-100 flex items-center justify-center">
              <Scissors className="h-6 w-6 text-pink-600" />
            </div>
            <div>
              <h3 className="font-semibold">Textile & Garment Construction</h3>
              <p className="text-sm text-muted-foreground">Design, sewing, and clothing</p>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`flex-1 cursor-pointer transition-all ${selectedSubject === 'food' ? 'border-2 border-orange-500 bg-orange-50' : 'hover:border-orange-300'}`}
          onClick={() => setSelectedSubject('food')}
        >
          <CardContent className="flex items-center gap-4 p-4">
            <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
              <UtensilsCrossed className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold">Food & Nutrition</h3>
              <p className="text-sm text-muted-foreground">Food choices and preparation</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="strands" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Strands & SLOs
          </TabsTrigger>
          <TabsTrigger value="scheme" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Scheme of Work
          </TabsTrigger>
          <TabsTrigger value="assessment" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Assessment
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {getSubjectIcon(selectedSubject)}
                {currentSubjectData?.name}
              </CardTitle>
              <CardDescription>
                {selectedYear} curriculum overview with strands and assessment weighting
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {currentSubjectData?.strands?.map((strand) => (
                  <Card key={strand.id} className="border-2">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`p-2 rounded-lg ${strand.color.split(' ')[0]}`}>
                            {getStrandIcon(strand.name)}
                          </div>
                          <CardTitle className="text-base">{strand.name}</CardTitle>
                        </div>
                        <Badge>{strand.weight}%</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Progress value={strand.weight} className="h-2 mb-3" />
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        {strand.topics.slice(0, 3).map((topic, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <CheckCircle2 className="h-3 w-3 text-green-500 mt-1 shrink-0" />
                            {topic}
                          </li>
                        ))}
                        {strand.topics.length > 3 && (
                          <li className="text-xs text-muted-foreground">+{strand.topics.length - 3} more topics</li>
                        )}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Year Progression */}
          <Card>
            <CardHeader>
              <CardTitle>Year Level Progression</CardTitle>
              <CardDescription>How content builds from Year 7 to Year 10</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {Object.entries(homeEconomicsCurriculum.progression || {}).map(([year, content]) => (
                  <div key={year} className={`p-4 rounded-lg border-2 ${year === selectedYear ? 'border-pink-500 bg-pink-50' : 'border-gray-200'}`}>
                    <h4 className="font-semibold mb-2">{year}</h4>
                    <p className="text-xs text-muted-foreground">
                      <strong>Textile:</strong> {content.textile}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      <strong>Food:</strong> {content.food}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Strands & SLOs Tab */}
        <TabsContent value="strands" className="space-y-4">
          {currentSubjectData?.strands?.map((strand) => (
            <Card key={strand.id}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${strand.color.split(' ')[0]}`}>
                    {getStrandIcon(strand.name)}
                  </div>
                  <div>
                    <CardTitle>{strand.name}</CardTitle>
                    <CardDescription>IE Weighting: {strand.weight}%</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      Key Topics
                    </h4>
                    <ul className="space-y-2">
                      {strand.topics.map((topic, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          {topic}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Student Learning Outcomes (SLOs)
                    </h4>
                    <ul className="space-y-2">
                      {strand.slos.map((slo, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm bg-gray-50 p-2 rounded">
                          <Badge variant="outline" className="shrink-0 text-xs">{idx + 1}</Badge>
                          {slo}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Scheme of Work Tab */}
        <TabsContent value="scheme" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>4-Term Scheme of Work</CardTitle>
                  <CardDescription>Weekly teaching plan for {selectedYear}</CardDescription>
                </div>
                <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="term1">Term 1</SelectItem>
                    <SelectItem value="term2">Term 2</SelectItem>
                    <SelectItem value="term3">Term 3</SelectItem>
                    <SelectItem value="term4">Term 4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Week</TableHead>
                    <TableHead>Topic</TableHead>
                    <TableHead className="w-[120px]">Subject</TableHead>
                    <TableHead>Activities</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schemeData?.[selectedTerm]?.weeks?.map((week) => (
                    <TableRow key={week.week}>
                      <TableCell className="font-medium">Week {week.week}</TableCell>
                      <TableCell className="font-medium">{week.topic}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${
                          week.subject === 'Textile' ? 'bg-pink-50 text-pink-700' : 
                          week.subject === 'Food' ? 'bg-orange-50 text-orange-700' : 
                          'bg-purple-50 text-purple-700'
                        }`}>
                          {week.subject}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{week.activities}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assessment Tab */}
        <TabsContent value="assessment" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Assessment Structure - {currentSubjectData?.name}</CardTitle>
              <CardDescription>How student performance is evaluated</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {currentSubjectData?.assessments?.map((assessment, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{assessment.name}</span>
                      <span className="font-bold">{assessment.weight}%</span>
                    </div>
                    <Progress value={assessment.weight} className="h-3" />
                    <p className="text-xs text-muted-foreground">{assessment.description}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Internal Examination (IE) - 60%
                  </h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Two-hour written examination</li>
                    <li>• Administered mid-year and end-of-year</li>
                    <li>• Covers all three strands</li>
                  </ul>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Continuous Assessment (CA) - 40%
                  </h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Teacher Design Tasks (TDT)</li>
                    <li>• Common Assessment Task (CAT)</li>
                    <li>• Practical work and projects</li>
                  </ul>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="font-semibold mb-3">IE Strand Weighting</h4>
                <div className="grid grid-cols-3 gap-4">
                  {currentSubjectData?.strands?.map((strand) => (
                    <div key={strand.id} className={`p-3 rounded-lg ${strand.color.split(' ')[0]} border`}>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">{strand.name}</span>
                        <Badge>{strand.weight}%</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
